import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileExplorer } from "@amzn/aws-euc-ui";
import { useFilesI18n } from "./useFilesI18n";
import { useFilesApis } from "./useFilesApis";
import { AppStreamSDK } from "@utils/AppStreamSDK";
import { INITIAL_FILE_EXPLORER_PATH } from "@constants/toolbar";
import { Alert, Button, StatusIndicator } from "@cloudscape-design/components";
import { EmbedConstants } from "@amzn/photon-portal-embed-sdk";
import { useSessionDataStore } from "@hooks/useSessionDataStore";
import { MetricName } from "@constants/logging";
import { useGetLogger } from "@hooks/useGetLogger";

interface FileToolbarItemContentProps {
    appStreamSDK: AppStreamSDK;
}

export const FileToolbarItemContent = (props: FileToolbarItemContentProps) => {
    const { t } = useTranslation();
    const log = useGetLogger();
    const i18nStrings = useFilesI18n();
    const api = useFilesApis(props.appStreamSDK);
    const isFileExplorerReady = useSessionDataStore(
        (state) => state.isFileExplorerReady
    );
    const setIsFileExplorerReady = useSessionDataStore(
        (state) => state.setIsFileExplorerReady
    );
    const [fileConnectionErrorMessage, setFileConnectionErrorMessage] =
        useState("");

    const setupFileExplorerConnection = useCallback(async () => {
        setIsFileExplorerReady(false);
        setFileConnectionErrorMessage("");
        try {
            await props.appStreamSDK.performActionPromise(
                EmbedConstants.METHOD_GET_FILE_EXPLORER
            );
            log.logMessage("File connection is ready");
            setIsFileExplorerReady(true);
            log.publishCounterMetric(
                MetricName.FILE_EXPLORER_CONNECTION_SUCCESS
            );
        } catch {
            log.logMessage("Failed to retry setting up file connection");
            setFileConnectionErrorMessage(
                t("toolbar.item.files.connectionFailed")
            );
            log.publishCounterMetric(
                MetricName.FILE_EXPLORER_CONNECTION_FAILURE
            );
        }
    }, [props.appStreamSDK]);

    useEffect(() => {
        if (!isFileExplorerReady) {
            void setupFileExplorerConnection();
        }
    }, [props.appStreamSDK]);

    const userInterface = useMemo(() => {
        if (isFileExplorerReady) {
            return props.appStreamSDK.getUserInterfaceState();
        } else {
            return {
                isFileDownloadAllowed: false,
                isFileUploadAllowed: false,
            };
        }
    }, [isFileExplorerReady]);

    if (fileConnectionErrorMessage) {
        return (
            <Alert
                type="error"
                action={
                    <Button onClick={setupFileExplorerConnection}>
                        {t("toolbar.item.files.retryConnectionButton.label")}
                    </Button>
                }
                header={fileConnectionErrorMessage}
            />
        );
    }

    if (!isFileExplorerReady) {
        return (
            <div className={"file-explorer-loading"}>
                <StatusIndicator type={"loading"}>
                    {t("toolbar.item.files.preparingConnection")}
                </StatusIndicator>
            </div>
        );
    }

    return (
        <FileExplorer
            api={api}
            i18nStrings={i18nStrings}
            rootPath={INITIAL_FILE_EXPLORER_PATH}
            retainPrevPath
            tableVariant={"embedded"}
            contentDensity={"compact"}
            textFilterProps={{
                filteringPlaceholder: t(
                    "toolbar.item.files.content.table.filtering.placeholder"
                ),
            }}
            allowedActions={{
                download: userInterface.isFileDownloadAllowed,
                upload: userInterface.isFileUploadAllowed,
            }}
        />
    );
};
